/**
 * Source Reader — investigative source overlay system.
 *
 * Strategic intent (Phase 7A):
 *   Keep users inside the TCP ecosystem when exploring source material.
 *   Preserve investigative continuity, rabbit-hole flow, and the calm
 *   documentary atmosphere — even when the source itself cannot be
 *   embedded.
 *
 * Two layers — chosen automatically per source:
 *
 *   LAYER 1 — Embedded primary evidence (PDFs, patents, court filings,
 *     FDA inserts, government documents, archive.org snapshots, internal
 *     evidence). Cinematic drawer with iframe. If the load times out or
 *     errors, gracefully degrades to Layer 2.
 *
 *   LAYER 2 — Contextual external sources (Wikipedia, news, company
 *     sites, WHO, press releases, anything else). NO iframe attempt.
 *     Drawer renders a rich contextual transition panel: source meta,
 *     why-this-matters, related investigations, related entities,
 *     timeline relevance, Wayback fallback, save/share, then a calm
 *     "Open original source" CTA.
 *
 * Public API:
 *   <SourceReaderProvider>          — wrap once at the app root
 *   useSourceReader()               — { openSource, closeSource, isOpen }
 *   openSource(source)              — call from any click handler
 *
 * Premium gating: this component never receives ghost sources (they have
 * no url + are non-clickable echoes). Members + preview-key users get
 * the full source object as the backend already returns.
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    X, ExternalLink, FileText, ArrowLeft, ShieldCheck, Network,
    Calendar, Globe2, Archive, Bookmark, BookmarkCheck, Link2, Check,
    AlertCircle, BookOpen, Building2, User, Landmark,
} from "lucide-react";
import { track } from "../lib/analytics";
import { entitySlug } from "./EntityCard";

// --------------------------------------------------------------------
// Layer classification
// --------------------------------------------------------------------

const EMBED_FRIENDLY_TYPES = new Set([
    "patent", "pdf", "court filing", "court_filing", "court document",
    "fda insert", "fda_insert", "government document", "government_document",
    "internal document", "internal_document", "archive", "archived document",
    "leaked document", "leaked_document", "regulatory filing",
]);

const EMBED_FRIENDLY_HOSTS = [
    /(^|\.)archive\.org$/i,
    /(^|\.)web\.archive\.org$/i,
    /(^|\.)courtlistener\.com$/i,
    /(^|\.)sec\.gov$/i,
    /(^|\.)govinfo\.gov$/i,
    /(^|\.)patents\.google\.com$/i,
    /(^|\.)patentscope\.wipo\.int$/i,
    /(^|\.)eur-lex\.europa\.eu$/i,
    /(^|\.)europa\.eu$/i,
    /(^|\.)congress\.gov$/i,
    /(^|\.)federalregister\.gov$/i,
];

function classifySource(source) {
    const docUrl = source?.document_url || "";
    const url = source?.url || "";
    const type = (source?.source_type_canonical || source?.source_type || "").toLowerCase().trim();
    const target = docUrl || url;
    if (!target) return { layer: "context", target: "" };

    const isPdf = /\.pdf([?#].*)?$/i.test(target);
    if (isPdf || docUrl) return { layer: "embed", target };

    if (EMBED_FRIENDLY_TYPES.has(type)) return { layer: "embed", target };

    try {
        const host = new URL(target).hostname;
        if (EMBED_FRIENDLY_HOSTS.some((re) => re.test(host))) {
            return { layer: "embed", target };
        }
    } catch { /* noop */ }

    return { layer: "context", target };
}

function waybackSearchUrl(originalUrl) {
    if (!originalUrl) return null;
    return `https://web.archive.org/web/*/${originalUrl}`;
}

// --------------------------------------------------------------------
// Bookmarks (same storage key as BookmarkButton — kept in sync)
// --------------------------------------------------------------------

const BOOKMARKS_KEY = "tcp:bookmarks";

function readBookmarks() {
    try {
        return JSON.parse(localStorage.getItem(BOOKMARKS_KEY) || "[]");
    } catch { return []; }
}

function toggleSourceBookmark(source) {
    const id = source.key || source.url || source.title;
    if (!id) return false;
    const bookmarks = readBookmarks();
    const idx = bookmarks.findIndex((b) => b.id === id && b.type === "source");
    let added;
    if (idx >= 0) {
        bookmarks.splice(idx, 1);
        added = false;
    } else {
        bookmarks.unshift({
            id,
            type: "source",
            title: source.title || "Untitled source",
            subtitle: source.publisher || source.source_type_canonical || "",
            url: source.url || source.document_url || "",
            saved_at: new Date().toISOString(),
        });
        added = true;
    }
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarks));
    window.dispatchEvent(new Event("tcp:bookmarks-updated"));
    track("tcp_bookmark", { action: added ? "add" : "remove", type: "source", id, source: "source_reader" });
    return added;
}

function isSourceBookmarked(source) {
    const id = source?.key || source?.url || source?.title;
    if (!id) return false;
    return readBookmarks().some((b) => b.id === id && b.type === "source");
}

// --------------------------------------------------------------------
// Context provider
// --------------------------------------------------------------------

const SourceReaderContext = createContext(null);

export function useSourceReader() {
    const ctx = useContext(SourceReaderContext);
    if (!ctx) return { openSource: () => {}, closeSource: () => {}, isOpen: false };
    return ctx;
}

export function SourceReaderProvider({ children }) {
    const [source, setSource] = useState(null);
    const location = useLocation();

    const openSource = useCallback((src) => {
        if (!src) return;
        setSource(src);
        const { layer } = classifySource(src);
        track("tcp_source_reader_open", {
            key: src.key || src.url,
            title: src.title,
            layer,
        });
    }, []);

    const closeSource = useCallback(() => setSource(null), []);

    // Auto-close on SPA navigation so the drawer never lingers on the next
    // page and body scroll-lock can be released by the open-effect cleanup.
    useEffect(() => {
        setSource(null);
    }, [location.pathname, location.search]);

    // Close on ESC
    useEffect(() => {
        if (!source) return;
        const onKey = (e) => { if (e.key === "Escape") closeSource(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [source, closeSource]);

    // Lock body scroll while open
    useEffect(() => {
        if (!source) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [source]);

    const value = useMemo(
        () => ({ openSource, closeSource, isOpen: !!source }),
        [openSource, closeSource, source]
    );

    return (
        <SourceReaderContext.Provider value={value}>
            {children}
            <SourceReaderDrawer source={source} onClose={closeSource} />
        </SourceReaderContext.Provider>
    );
}

// --------------------------------------------------------------------
// Drawer
// --------------------------------------------------------------------

function SourceReaderDrawer({ source, onClose }) {
    if (!source) return null;

    return (
        <div
            data-testid="source-reader-overlay"
            className="fixed inset-0 z-[80] flex"
            role="dialog"
            aria-modal="true"
            aria-label="Source reader"
        >
            {/* Backdrop */}
            <button
                type="button"
                data-testid="source-reader-backdrop"
                onClick={onClose}
                aria-label="Close source reader"
                className="absolute inset-0 bg-[#04060B]/85 cursor-default animate-[fadeIn_0.35s_ease-out]"
                style={{ backdropFilter: "saturate(1.1)" }}
            />
            {/* Panel — full-screen on mobile, right drawer on desktop */}
            <div
                className="ml-auto relative h-full w-full md:w-[860px] lg:w-[1040px] bg-[#0B1220] border-l border-[#243044] overflow-hidden flex flex-col animate-[slideInRight_0.42s_cubic-bezier(0.22,1,0.36,1)] shadow-[-24px_0_60px_-20px_rgba(0,0,0,0.7)]"
                style={{ willChange: "transform" }}
            >
                <DrawerInner source={source} onClose={onClose} />
            </div>
            <style>{`
                @keyframes fadeIn { from{opacity:0} to{opacity:1} }
                @keyframes slideInRight { from{transform:translateX(40px);opacity:0} to{transform:translateX(0);opacity:1} }
            `}</style>
        </div>
    );
}

function DrawerInner({ source, onClose }) {
    const { layer, target } = useMemo(() => classifySource(source), [source]);
    return (
        <>
            <DrawerHeader source={source} onClose={onClose} layer={layer} />
            <div className="flex-1 min-h-0 overflow-y-auto">
                {layer === "embed" ? (
                    <EmbedView source={source} target={target} />
                ) : (
                    <ContextView source={source} />
                )}
            </div>
            <DrawerFooter source={source} target={target} />
        </>
    );
}

function DrawerHeader({ source, onClose, layer }) {
    const meta = [source.publisher, source.source_type_canonical || source.source_type, source.date]
        .filter(Boolean).join(" · ");
    return (
        <header className="shrink-0 border-b border-[#243044] bg-[#0B1220]">
            <div className="px-5 md:px-7 py-4 flex items-start gap-4">
                <button
                    type="button"
                    data-testid="source-reader-close"
                    onClick={onClose}
                    className="shrink-0 w-9 h-9 rounded-full bg-[#111827] border border-[#243044] hover:border-[#2F80ED] flex items-center justify-center text-[#94A3B8] hover:text-white transition-colors"
                    aria-label="Back to investigation"
                >
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-semibold inline-flex items-center gap-1.5">
                            {layer === "embed"
                                ? (<><FileText className="w-3 h-3" /> Source archive</>)
                                : (<><Globe2 className="w-3 h-3" /> External source · context</>)}
                        </span>
                    </div>
                    <h2
                        data-testid="source-reader-title"
                        className="text-base md:text-lg font-semibold text-white leading-snug line-clamp-2"
                    >
                        {source.title || "Cited source"}
                    </h2>
                    {meta && (
                        <p className="text-xs text-[#64748B] mt-1 truncate">{meta}</p>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="source-reader-close-x"
                    onClick={onClose}
                    aria-label="Close"
                    className="shrink-0 md:hidden w-9 h-9 rounded-full bg-[#111827] border border-[#243044] flex items-center justify-center text-[#94A3B8] hover:text-white"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </header>
    );
}

// ---- Embed view (Layer 1) -------------------------------------------

function EmbedView({ source, target }) {
    const [status, setStatus] = useState("loading"); // loading | embedded | blocked
    const iframeRef = useRef(null);
    const timeoutRef = useRef(null);

    useEffect(() => {
        setStatus("loading");
        // 6-second grace window. If onLoad hasn't fired by then, assume blocked.
        timeoutRef.current = setTimeout(() => {
            setStatus((s) => {
                if (s === "loading") {
                    track("tcp_source_reader_embed_status", { key: source.key || source.url || target, status: "blocked", reason: "timeout", target });
                    return "blocked";
                }
                return s;
            });
        }, 6000);
        return () => clearTimeout(timeoutRef.current);
    }, [target, source.key, source.url]);

    const onLoad = () => {
        setStatus("embedded");
        track("tcp_source_reader_embed_status", { key: source.key || source.url || target, status: "embedded", target });
        clearTimeout(timeoutRef.current);
    };
    const onError = () => {
        setStatus("blocked");
        track("tcp_source_reader_embed_status", { key: source.key || source.url || target, status: "blocked", reason: "error", target });
        clearTimeout(timeoutRef.current);
    };

    if (status === "blocked") {
        // Soft fallback — Layer 2 inside Layer 1
        return (
            <div className="p-6 md:p-10">
                <FallbackCard
                    source={source}
                    target={target}
                    reason="This document cannot be embedded directly."
                />
                <div className="mt-8">
                    <ContextView source={source} compact />
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-full min-h-[60vh] bg-[#04060B]">
            {status === "loading" && (
                <div data-testid="source-reader-loading" className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 pointer-events-none">
                    <div className="w-2 h-2 rounded-full bg-[#56CCF2] animate-pulse mb-4" />
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2]">Loading source archive</p>
                    <p className="text-sm text-[#94A3B8] mt-2 max-w-md leading-relaxed">
                        {source.title}
                    </p>
                </div>
            )}
            <iframe
                ref={iframeRef}
                data-testid="source-reader-iframe"
                title={source.title || "Source"}
                src={target}
                onLoad={onLoad}
                onError={onError}
                referrerPolicy="no-referrer"
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
                className="w-full h-full min-h-[60vh] border-0 bg-white"
            />
        </div>
    );
}

function FallbackCard({ source, target, reason }) {
    const wayback = waybackSearchUrl(target);
    return (
        <div data-testid="source-reader-fallback" className="bg-[#111827] border border-[#243044] rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="w-4 h-4 mt-1 text-[#56CCF2] shrink-0" />
                <div>
                    <p className="text-sm font-semibold text-white">{reason}</p>
                    <p className="text-xs text-[#64748B] mt-1">
                        We've preserved the citation, the context, and the connections. Open the original below — or trace it through the Wayback archive.
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
                <OpenOriginalBtn source={source} target={target} primary />
                {wayback && (
                    <a
                        href={wayback}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="source-reader-wayback"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#111827] border border-[#243044] hover:border-[#2F80ED] text-xs text-[#CBD5E1] hover:text-white transition-colors"
                    >
                        <Archive className="w-3.5 h-3.5" />
                        Search the Wayback archive
                    </a>
                )}
            </div>
        </div>
    );
}

// ---- Context view (Layer 2) -----------------------------------------

function ContextView({ source, compact = false }) {
    const target = source.document_url || source.url || "";
    const wayback = waybackSearchUrl(target);
    return (
        <div className={compact ? "" : "p-6 md:p-10 space-y-8"}>
            {!compact && <WhyThisMatters source={source} />}
            <RelatedInvestigations source={source} />
            <RelatedEntities source={source} />
            <TimelineRelevance source={source} />
            {!compact && (
                <section data-testid="source-reader-actions" className="pt-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-3">Open the source</div>
                    <div className="flex flex-wrap gap-3">
                        <OpenOriginalBtn source={source} target={target} primary />
                        {wayback && (
                            <a
                                href={wayback}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="source-reader-wayback"
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#111827] border border-[#243044] hover:border-[#2F80ED] text-xs text-[#CBD5E1] hover:text-white transition-colors"
                            >
                                <Archive className="w-3.5 h-3.5" />
                                Search the Wayback archive
                            </a>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
}

function WhyThisMatters({ source }) {
    const reason = source.notes || source.context_summary;
    if (!reason && !source.funding && !source.verified_at) return null;
    return (
        <section data-testid="source-reader-why">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-3 inline-flex items-center gap-1.5">
                <BookOpen className="w-3 h-3" />
                Why this source matters
            </div>
            {reason ? (
                <p className="text-sm md:text-[15px] text-[#CBD5E1] leading-relaxed">{reason}</p>
            ) : (
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                    {source.publisher ? `${source.publisher} — ` : ""}
                    {source.source_type_canonical || source.source_type || "Cited source"} on record across {(source.topics || []).length || "this"} investigation{(source.topics || []).length === 1 ? "" : "s"}.
                </p>
            )}
            <div className="flex flex-wrap items-center gap-3 mt-4 text-[10px] uppercase tracking-[0.18em] text-[#64748B]">
                {source.funding && (
                    <span className="inline-flex items-center gap-1.5">
                        <ShieldCheck className="w-3 h-3 text-[#56CCF2]" />
                        {source.funding}
                    </span>
                )}
                {source.verified_at && (
                    <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 text-[#56CCF2]" />
                        Verified {new Date(source.verified_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                    </span>
                )}
            </div>
        </section>
    );
}

function RelatedInvestigations({ source }) {
    const topics = source.topics || [];
    if (!topics.length) return null;
    return (
        <section data-testid="source-reader-related">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-3 inline-flex items-center gap-1.5">
                <Network className="w-3 h-3" />
                Cited in {topics.length} investigation{topics.length === 1 ? "" : "s"}
            </div>
            <ul className="space-y-2">
                {topics.map((t) => (
                    <li key={t.slug}>
                        <Link
                            to={`/topic/${t.slug}`}
                            data-testid={`source-reader-topic-${t.slug}`}
                            className="group flex items-start gap-3 px-4 py-3 rounded-xl bg-[#111827] border border-[#243044] hover:border-[#2F80ED] transition-colors"
                        >
                            <span className="text-[10px] uppercase tracking-wider text-[#56CCF2] mt-1 shrink-0">{t.category}</span>
                            <span className="text-sm text-[#CBD5E1] group-hover:text-white leading-snug">{t.title}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function RelatedEntities({ source }) {
    const entities = source.graph?.entities || [];
    if (!entities.length) return null;
    return (
        <section data-testid="source-reader-entities">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-3">
                Entities referenced alongside this source
            </div>
            <div className="flex flex-wrap gap-1.5">
                {entities.map((e, i) => {
                    const slug = entitySlug(e.name);
                    const Icon = e.type === "person" ? User : e.type === "company" ? Building2 : Landmark;
                    const inner = (
                        <span className="inline-flex items-center gap-1.5">
                            <Icon className="w-3 h-3 text-[#56CCF2]" />
                            {e.name}
                        </span>
                    );
                    const cls = "px-3 py-1.5 rounded-full bg-[#111827] border border-[#243044] text-xs text-[#CBD5E1] hover:border-[#2F80ED] hover:text-white transition-colors";
                    return slug ? (
                        <Link key={i} to={`/entity/${slug}`} className={cls}>{inner}</Link>
                    ) : (
                        <span key={i} className={cls}>{inner}</span>
                    );
                })}
            </div>
        </section>
    );
}

function TimelineRelevance({ source }) {
    const events = source.graph?.timeline_events || [];
    if (!events.length) return null;
    return (
        <section data-testid="source-reader-timeline">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] mb-3 inline-flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />
                Timeline references
            </div>
            <ul className="space-y-2">
                {events.slice(0, 8).map((ev, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-[#56CCF2] mt-0.5 min-w-[60px]">{ev.date}</span>
                        <span className="text-[#CBD5E1] leading-snug">{ev.title}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function OpenOriginalBtn({ source, target, primary }) {
    if (!target) return null;
    const onClick = () => track("tcp_source_reader_open_original", {
        key: source.key || source.url,
        target,
    });
    if (primary) {
        return (
            <a
                href={target}
                target="_blank"
                rel="noreferrer"
                onClick={onClick}
                data-testid="source-reader-open-original"
                className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#56CCF2] text-[#0B1220] text-xs font-semibold tracking-wide hover:bg-white transition-colors"
            >
                Open original source
                <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
        );
    }
    return (
        <a
            href={target}
            target="_blank"
            rel="noreferrer"
            onClick={onClick}
            data-testid="source-reader-open-original"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111827] border border-[#243044] hover:border-[#2F80ED] text-xs text-[#CBD5E1] hover:text-white transition-colors"
        >
            <ExternalLink className="w-3 h-3" />
            Open original
        </a>
    );
}

// ---- Footer with save / share / open ----

function DrawerFooter({ source, target }) {
    const navigate = useNavigate();
    const [bookmarked, setBookmarked] = useState(() => isSourceBookmarked(source));
    const [copied, setCopied] = useState(false);

    useEffect(() => { setBookmarked(isSourceBookmarked(source)); }, [source]);

    const onToggleBookmark = () => {
        const added = toggleSourceBookmark(source);
        setBookmarked(added);
    };

    const shareUrl = useMemo(() => {
        const base = process.env.REACT_APP_PUBLIC_SITE_URL
            || (typeof window !== "undefined" ? window.location.origin : "");
        const key = source.key || "";
        return key ? `${base}/sources?source=${encodeURIComponent(key)}` : base;
    }, [source]);

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch {
            // Fallback for older browsers / insecure contexts
            try {
                const ta = document.createElement("textarea");
                ta.value = shareUrl;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
                setCopied(true);
                setTimeout(() => setCopied(false), 2200);
            } catch { /* surface nothing — failure is rare and silent is calmer than a toast */ }
        }
        track("tcp_share", { destination: "copy", content_type: "source", content_id: source.key, url: shareUrl, source: "source_reader" });
    };

    const openInVault = () => {
        // Close first so the drawer animates out cleanly, then navigate.
        // (The location-change effect also closes it — this is belt-and-braces.)
        if (!source.key) return;
        navigate(`/sources?source=${encodeURIComponent(source.key)}`);
    };

    return (
        <footer className="shrink-0 border-t border-[#243044] bg-[#0B1220] px-5 md:px-7 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="source-reader-bookmark"
                        onClick={onToggleBookmark}
                        title={bookmarked ? "Saved source · click to remove" : "Save source"}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs transition-colors ${bookmarked ? "bg-[#56CCF2]/10 border border-[#56CCF2]/40 text-[#56CCF2]" : "bg-[#111827] border border-[#243044] hover:border-[#2F80ED] text-[#CBD5E1]"}`}
                    >
                        {bookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                        {bookmarked ? "Saved" : "Save source"}
                    </button>
                    <button
                        type="button"
                        data-testid="source-reader-copy"
                        onClick={onCopy}
                        title="Copy share link"
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs transition-colors ${copied ? "bg-[#56CCF2]/10 border border-[#56CCF2]/40 text-[#56CCF2]" : "bg-[#111827] border border-[#243044] hover:border-[#2F80ED] text-[#CBD5E1]"}`}
                    >
                        {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                        {copied ? "Copied" : "Copy link"}
                    </button>
                    {source.key && (
                        <button
                            type="button"
                            data-testid="source-reader-open-vault"
                            onClick={openInVault}
                            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs bg-[#111827] border border-[#243044] hover:border-[#2F80ED] text-[#CBD5E1] hover:text-white transition-colors"
                        >
                            <Network className="w-3.5 h-3.5" />
                            Open as research node
                        </button>
                    )}
                </div>
                <OpenOriginalBtn source={source} target={target} primary />
            </div>
        </footer>
    );
}
